import {
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  DrawerContent,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  useDisclosure,
  addToast,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { Pencil, Trash2, Search, Languages, Plus } from "lucide-react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { useState, useEffect } from "react";
import { api, queryClient } from "@/lib/api-client";
import { getLocalizedValue } from "@/utils";
import { useForm } from "@mantine/form";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import { LocalizationForm } from "@/Components/NextBase/LocalizationForm";
import {
  SUPPORTED_LOCALES,
  Locale,
} from "../../../../../../../shared/constants/locales";
import {
  GetSubscriptionCategories,
  GetSubscriptionCategory,
} from "@/server-types";
import { useMediaQuery } from "@mantine/hooks";
import { AlertButton } from "@/Components/NextBase/AlertButton";
import { useSearchParams } from "react-router";

type Category = GetSubscriptionCategories["response"]["categories"][number];
type CategoryNameLocalized =
  GetSubscriptionCategory["response"]["category"]["nameLocalized"][number];

const CategoriesTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQueryRaw = searchParams.get("q");
  const searchQuery = searchQueryRaw ?? "";

  const { data: categoriesData = { categories: [] } } =
    api.subscription.useGetSubscriptionCategories();
  const { mutateAsync: deleteCategory } =
    api.subscription.useDeleteSubscriptionCategory();

  const filteredCategories = categoriesData.categories?.filter(
    (category: Category) =>
      category.nameLocalized.some((name: CategoryNameLocalized) =>
        name.value.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | undefined
  >(undefined);

  const {
    isOpen: isCategoryFormOpen,
    onOpen: onCategoryFormOpen,
    onClose: onCategoryFormClose,
  } = useDisclosure();

  const isMobile = useMediaQuery("(max-width: 640px)");

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <LimeInput
            type="search"
            placeholder={t`Išči kategorije...`}
            startContent={<Search className="text-gray-400" size={16} />}
            value={searchQuery}
            autoFocus
            onValueChange={(e) => {
              setSearchParams((prev) => {
                if (e === "") {
                  prev.delete("q");
                } else {
                  prev.set("q", e);
                }
                return prev;
              });
            }}
            variant="bordered"
          />
        </div>
        <Button
          size="sm"
          startContent={<Plus size={16} />}
          isIconOnly={isMobile}
          onPress={() => {
            setSelectedCategoryId(undefined);
            onCategoryFormOpen();
          }}
        >
          <span className="hidden sm:block">
            <Trans>Dodaj kategorijo</Trans>
          </span>
        </Button>
      </div>
      <Table aria-label="Categories table">
        <TableHeader>
          <TableColumn key="label">{t`Kategorija`}</TableColumn>
          <TableColumn key="items">{t`Število naročnin`}</TableColumn>
          <TableColumn key="actions" className="w-1">{t`Akcije`}</TableColumn>
        </TableHeader>
        <TableBody
          items={filteredCategories}
          emptyContent={
            <span className="text-gray-400">
              <Trans>Ni kategorij</Trans>
            </span>
          }
        >
          {(category: Category) => {
            return (
              <TableRow key={category.id}>
                <TableCell>
                  <span>{getLocalizedValue(category.nameLocalized)}</span>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center justify-center rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                    {category.subscriptions.length}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      isIconOnly
                      variant="flat"
                      onPress={() => {
                        setSelectedCategoryId(category.id);
                        onCategoryFormOpen();
                      }}
                    >
                      <Pencil size={16} />
                    </Button>
                    <AlertButton
                      title={t`Izbriši kategorijo`}
                      content={t`Ali ste prepričani, da želite izbrisati kategorijo?`}
                      buttonProps={{
                        isIconOnly: true,
                        size: "sm",
                        color: "danger",
                        variant: "flat",
                      }}
                      confirmProps={{
                        label: t`Izbriši`,
                        onClick: async () => {
                          try {
                            await deleteCategory(category.id);
                            addToast({
                              title: t`Kategorija izbrisana`,
                              color: "success",
                            });
                          } catch (error) {
                            addToast({
                              title: t`Napaka pri izbrisu kategorije`,
                              color: "danger",
                            });
                          }
                          queryClient.invalidateQueries({
                            queryKey: ["subscriptionCategories"],
                          });
                        },
                      }}
                    >
                      <Trash2 size={16} />
                    </AlertButton>
                  </div>
                </TableCell>
              </TableRow>
            );
          }}
        </TableBody>
      </Table>
      <CategoryForm
        isOpen={isCategoryFormOpen}
        onClose={onCategoryFormClose}
        categoryId={selectedCategoryId}
      />
    </>
  );
};

export const CategoryForm = ({
  categoryId,
  isOpen,
  onClose,
}: {
  categoryId?: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { data: category } = api.subscription.useGetSubscriptionCategory(
    categoryId ?? -1,
  );
  const { mutateAsync: createCategory } =
    api.subscription.usePostSubscriptionCategory();
  const { mutateAsync: updateCategory } =
    api.subscription.usePutSubscriptionCategory();

  const { preferredLanguage } = usePreferredLanguageStore((state) => state);
  const defaultLanguage =
    preferredLanguage.userPreferredLanguage ||
    preferredLanguage.clientPreferredLanguage ||
    "sl";
  const [isNameLocalizationOpen, setNameLocalizationOpen] = useState(false);

  const form = useForm<{
    nameLocalized: { language: Locale; value: string }[];
  }>({
    initialValues: {
      nameLocalized: SUPPORTED_LOCALES.map((language: Locale) => ({
        language,
        value: "",
      })),
    },
  });

  useEffect(() => {
    if (category?.category) {
      form.setValues({
        nameLocalized: SUPPORTED_LOCALES.map((language: Locale) => {
          const found = category.category.nameLocalized?.find(
            (n: CategoryNameLocalized) => n.language === language,
          );
          return found
            ? { language: found.language as Locale, value: found.value }
            : { language, value: "" };
        }),
      });
    }
  }, [category]);

  const isEditing = !!categoryId;

  const handleSubmit = async () => {
    console.log("Submitting form");
    try {
      if (isEditing) {
        await updateCategory({ categoryId, body: form.values });
      } else {
        await createCategory(form.values);
      }
      addToast({
        title: `${t`Kategorija`} ${isEditing ? t`posodobljena` : t`dodana`}`,
        description: `${t`Kategorija`} ${isEditing ? t`posodobljena` : t`dodana`}`,
        color: "success",
      });
      onClose();
      queryClient.invalidateQueries({
        queryKey: ["subscriptionCategories"],
      });
    } catch (error) {
      addToast({
        title: t`Napaka pri shranjevanju kategorije`,
        description: t`Poskusite znova`,
        color: "danger",
      });
    }
  };

  const defaultLangIndex = form.values.nameLocalized.findIndex(
    (n) => n.language === defaultLanguage,
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.onSubmit(handleSubmit)(e);
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <h1>{isEditing ? t`Uredi kategorijo` : t`Dodaj kategorijo`}</h1>
          </DrawerHeader>
          <DrawerBody>
            <div className="flex flex-col gap-4">
              <LimeInput
                type="text"
                variant="bordered"
                label={t`Ime kategorije`}
                labelPlacement="outside"
                placeholder={t`Vnesi ime kategorije`}
                {...form.getInputProps(
                  `nameLocalized.${defaultLangIndex}.value`,
                )}
                endContent={
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    onPress={() => setNameLocalizationOpen(true)}
                  >
                    <Languages size={16} />
                  </Button>
                }
              />
            </div>
            <LocalizationForm
              opened={isNameLocalizationOpen}
              localized={form.values.nameLocalized}
              save={(values) => {
                form.setFieldValue("nameLocalized", values);
                setNameLocalizationOpen(false);
              }}
              cancel={() => setNameLocalizationOpen(false)}
              inputLabel={t`Ime kategorije`}
            />
          </DrawerBody>
          <DrawerFooter>
            <Button variant="light" onPress={onClose}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button type="submit" color="primary">
              {isEditing ? t`Uredi` : t`Dodaj`}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </form>
    </Drawer>
  );
};

export default CategoriesTab;
